import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, User, Clock, Facebook, Twitter, Linkedin, Share } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import CTASection from '@/components/CTASection';
import BlogSEO from '@/components/seo/BlogSEO';
import { blogPosts } from '@/data/blogPosts';
import { toast } from '@/hooks/use-toast';
import { sendContactForm } from '@/utils/contactFormService';

const BlogPost = () => {
  const { postId } = useParams<{ postId: string }>();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Find the blog post with the matching ID
  const post = blogPosts.find((post) => post.id === postId);

  // Anonymize company names and people's names in content if present
  const anonymizeContent = (content: string | undefined): string => {
    if (!content) return '';
    
    // First replace company names with generic terms
    let processedContent = content
      .replace(/Techsolutions/g, "een technologiebedrijf")
      .replace(/ManufacturePro/g, "een productiebedrijf")
      .replace(/SalesTech Inc\./g, "een B2B sales organisatie")
      .replace(/DataCorp/g, "een databedrijf")
      .replace(/TeleSales Pro/g, "een telemarketing bureau")
      .replace(/MarketLeaders/g, "een marktleider")
      .replace(/CloudSys/g, "een cloud provider")
      .replace(/SoftwareCo/g, "een softwarebedrijf")
      .replace(/TechGiants/g, "grote technologiebedrijven")
      .replace(/FinTech Solutions/g, "een fintech bedrijf")
      .replace(/RetailMasters/g, "een retailbedrijf");
    
    // Then replace people's full names with just first names, except for Lambrecht
    // First, the specific patterns we already had
    processedContent = processedContent
      // Keep Lambrecht as is
      .replace(/(?<!Bert )Lambrecht/g, "Lambrecht")
      // Common Dutch/Belgian names with surnames
      .replace(/Jan Janssen/g, "Jan")
      .replace(/Pieter Peeters/g, "Pieter")
      .replace(/Mark Van den Berg/g, "Mark")
      .replace(/Thomas De Vries/g, "Thomas")
      .replace(/Sven Hendrickx/g, "Sven")
      .replace(/Koen De Smet/g, "Koen")
      .replace(/Dirk Van Damme/g, "Dirk")
      .replace(/Steven Willems/g, "Steven")
      .replace(/Johan Martens/g, "Johan")
      .replace(/David De Backer/g, "David")
      .replace(/Michael Jacobs/g, "Michael")
      .replace(/Erik Vandenberg/g, "Erik")
      .replace(/Frank Verhoeven/g, "Frank")
      .replace(/Hans Verstraeten/g, "Hans")
      .replace(/Robert Vermeulen/g, "Robert")
      // International names
      .replace(/John Smith/g, "John")
      .replace(/Mike Johnson/g, "Mike")
      .replace(/Sarah Williams/g, "Sarah")
      .replace(/David Brown/g, "David")
      .replace(/Richard Taylor/g, "Richard")
      .replace(/James Wilson/g, "James")
      .replace(/Thomas Anderson/g, "Thomas")
      .replace(/Jennifer Davis/g, "Jennifer")
      .replace(/Lisa Miller/g, "Lisa")
      .replace(/Elizabeth Moore/g, "Elizabeth")
      .replace(/Patricia Martin/g, "Patricia")
      .replace(/Robert Thompson/g, "Robert");
      
    // Now add additional specific name patterns we've found in content
    processedContent = processedContent
      .replace(/Jan Verhaeghe/g, "Jan")
      .replace(/Sophie Desmet/g, "Sophie")
      .replace(/Koen Verlinden/g, "Koen")
      .replace(/Tom Vanderstraeten/g, "Tom")
      .replace(/Peter De Ridder/g, "Peter")
      .replace(/Marc Wijnants/g, "Marc")
      .replace(/Bart Peeters/g, "Bart")
      .replace(/Marie Van Hout/g, "Marie")
      .replace(/Ann Claes/g, "Ann")
      .replace(/Luc Verbeke/g, "Luc");
      
    // Add a generic regex pattern to catch any remaining "Firstname Lastname" patterns
    // This will match any capitalized first name followed by capitalized last name(s)
    // but will exclude "Bert Lambrecht" and other names containing "Lambrecht"
    processedContent = processedContent.replace(
      /\b([A-Z][a-z]+)(?!\s+Lambrecht)\s+([A-Z][a-zéèêëäöüïàâôû\-']+(?:\s+[A-Z][a-zéèêëäöüïàâôû\-']+)*)\b/g,
      "$1"
    );
    
    // Handle quoted speech with names
    processedContent = processedContent.replace(
      /([""].*?)([A-Z][a-z]+)(?!\s+Lambrecht)\s+([A-Z][a-zéèêëäöüïàâôû\-']+(?:\s+[A-Z][a-zéèêëäöüïàâôû\-']+)*)(.*?[""])/g,
      "$1$2$4"
    );
    
    return processedContent;
  };

  useEffect(() => {
    // If the post doesn't exist, redirect to the 404 page
    if (!postId || !post) {
      console.error(`Blog post not found with ID: ${postId}`);
      navigate('/404');
    } else {
      console.log(`Rendering blog post: ${post.title}`);
    }
  }, [postId, post, navigate]);

  // Share functionality
  const handleShare = (platform: 'facebook' | 'twitter' | 'linkedin' | 'copy') => {
    const currentUrl = window.location.href;
    const postTitle = post?.title || 'Afsprakenmaker Blog';
    
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`;
        window.open(shareUrl, '_blank', 'width=600,height=400');
        break;
        
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(currentUrl)}&text=${encodeURIComponent(postTitle)}`;
        window.open(shareUrl, '_blank', 'width=600,height=400');
        break;
        
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(currentUrl)}`;
        window.open(shareUrl, '_blank', 'width=600,height=400');
        break;
        
      case 'copy':
        navigator.clipboard.writeText(currentUrl)
          .then(() => {
            toast({
              title: "Link gekopieerd",
              description: "De link naar dit artikel is naar je klembord gekopieerd.",
              duration: 3000,
            });
          })
          .catch((err) => {
            console.error('Kon link niet kopiëren:', err);
            toast({
              title: "Probleem bij kopiëren",
              description: "De link kon niet worden gekopieerd. Probeer het opnieuw.",
              variant: "destructive",
              duration: 3000,
            });
          });
        break;
    }
  };

  // Newsletter submission handler
  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      toast({
        title: "E-mailadres ontbreekt",
        description: "Vul een geldig e-mailadres in om je in te schrijven.",
        variant: "destructive",
        duration: 3000,
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Get the current URL and blog post title for the email
      const currentUrl = window.location.href;
      const blogTitle = post?.title || 'Onbekend artikel';
      
      // Prepare data for the email
      const newsletterData = {
        email: email,
        naam: "Nieuwsbrief inschrijving",
        bericht: `Nieuwe nieuwsbrief inschrijving van: ${email}`,
      };
      
      // Add additional info about which blog post
      const additionalInfo = {
        "Blog pagina": blogTitle,
        "URL": currentUrl,
        "Type aanvraag": "Nieuwsbrief inschrijving"
      };
      
      // Send email using the contactFormService
      const result = await sendContactForm(
        newsletterData,
        "Nieuwsbrief inschrijving via blog",
        additionalInfo
      );
      
      if (result) {
        toast({
          title: "Bedankt voor je inschrijving!",
          description: "Je ontvangt binnenkort onze nieuwsbrief.",
          duration: 3000,
        });
        setEmail('');
      } else {
        throw new Error("Fout bij versturen");
      }
    } catch (error) {
      console.error("Error submitting newsletter form:", error);
      toast({
        title: "Er ging iets mis",
        description: "We konden je inschrijving niet verwerken. Probeer het later opnieuw.",
        variant: "destructive",
        duration: 4000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // If post data is not available, show loading state
  if (!post) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <p className="text-xl">Loading article...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Extract tags from post or use default
  const postTags = post.tags || ["B2B Leadgeneratie", "Sales Optimalisatie"];
  
  // Generate ISO formatted date strings
  const publishDateISO = (() => {
    // Extract date parts from the Dutch format (e.g. "15 mei 2025")
    const dateParts = post.date.split(' ');
    if (dateParts.length !== 3) return new Date().toISOString();
    
    const day = parseInt(dateParts[0]);
    const monthMap: {[key: string]: number} = {
      'januari': 0, 'februari': 1, 'maart': 2, 'april': 3, 'mei': 4, 'juni': 5,
      'juli': 6, 'augustus': 7, 'september': 8, 'oktober': 9, 'november': 10, 'december': 11
    };
    const month = monthMap[dateParts[1].toLowerCase()];
    const year = parseInt(dateParts[2]);
    
    if (isNaN(day) || month === undefined || isNaN(year)) return new Date().toISOString();
    return new Date(year, month, day).toISOString();
  })();

  // Format read time for structured data
  const readTimeMinutes = parseInt(post?.readTime.split(' ')[0] || '0');

  return (
    <div className="min-h-screen flex flex-col">
      {/* Enhanced SEO metadata */}
      <BlogSEO
        title={`${post.title} | Afsprakenmaker Blog`}
        description={post.excerpt}
        keywords={postTags.join(', ')}
        canonicalUrl={`/blog/${post.id}`}
        ogImage={post.image}
        publishDate={publishDateISO}
        modifiedDate={publishDateISO}
        author={post.author}
        category="B2B Sales"
        tags={postTags}
      />
      
      <Navbar />

      {/* Hero Section */}
      <div className="bg-brand-lightBlue py-16 pt-24">
        <div className="container">
          <Link 
            to="/blog" 
            className="inline-flex items-center gap-2 text-brand-blue hover:underline mb-6"
          >
            <ArrowLeft size={16} />
            <span>Terug naar alle artikelen</span>
          </Link>
          
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-8 text-gray-900">
            {post?.title}
          </h1>
          
          <div className="flex flex-wrap items-center gap-6 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              <span>{post?.date}</span>
            </div>
            <div className="flex items-center gap-2">
              <User className="h-4 w-4" />
              <span>{post?.author}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <span>{post?.readTime}</span>
            </div>
            {post?.source && (
              <div>
                <span className="text-gray-500">Bron: </span>
                <a 
                  href={post.sourceUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-brand-blue hover:underline"
                >
                  {post.source}
                </a>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content Section - Enhanced styling with justified text */}
      <div className="py-12">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              {/* Featured Image */}
              <div className="mb-8">
                <img 
                  src={post?.image} 
                  alt={post?.title}
                  className="rounded-lg shadow-md w-full h-auto"
                />
              </div>

              {/* Article Content - Enhanced with justified text and proper spacing */}
              <article 
                className="prose prose-lg max-w-none prose-headings:text-gray-800 prose-headings:font-semibold prose-headings:mt-16 prose-headings:mb-8 prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-8 prose-p:text-justify prose-li:text-gray-700 prose-li:mb-4 prose-strong:text-gray-800" 
                itemScope 
                itemType="https://schema.org/Article"
              >
                {/* Hidden metadata for search engines */}
                <meta itemProp="headline" content={post?.title || ''} />
                <meta itemProp="author" content={post?.author || ''} />
                <meta itemProp="datePublished" content={publishDateISO || ''} />
                <meta itemProp="image" content={post?.image || ''} />
                
                {/* Article content - first the lead paragraph/excerpt */}
                <p className="text-xl font-medium leading-relaxed mb-10 text-gray-800 text-justify">
                  {post?.excerpt}
                </p>

                {/* Render post content with anonymized company names and people's names */}
                {post?.content && (
                  <div 
                    className="text-justify" 
                    dangerouslySetInnerHTML={{ 
                      __html: anonymizeContent(post.content).replace(
                        /<h([1-6])>(.*?)<\/h([1-6])>/g,
                        '<h$1 class="text-2xl font-bold mt-16 mb-8">$2</h$3>'
                      ).replace(
                        /<p>(.*?)<\/p>/g, 
                        '<p class="mb-8 text-justify">$1</p>'
                      )
                    }} 
                  />
                )}
                
                {/* Source reference if applicable */}
                {post?.sourceUrl && (
                  <div className="mt-12 p-6 bg-gray-50 rounded-lg border border-gray-100 shadow-sm">
                    <p className="text-sm text-gray-600 text-justify">
                      Dit artikel is oorspronkelijk gepubliceerd op{' '}
                      <a 
                        href={post.sourceUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-brand-blue hover:underline font-medium"
                      >
                        {post.source}.
                      </a> Bezoek de bron voor het volledige artikel.
                    </p>
                  </div>
                )}
              </article>
              
              {/* Share buttons */}
              <div className="mt-16">
                <h3 className="text-lg font-semibold mb-4">Deel dit artikel</h3>
                <div className="flex gap-3">
                  <button 
                    className="p-2 bg-[#3b5998] text-white rounded hover:bg-opacity-90 transition-colors" 
                    onClick={() => handleShare('facebook')}
                    aria-label="Deel op Facebook"
                  >
                    <Facebook size={20} />
                  </button>
                  <button 
                    className="p-2 bg-[#1DA1F2] text-white rounded hover:bg-opacity-90 transition-colors" 
                    onClick={() => handleShare('twitter')}
                    aria-label="Deel op Twitter"
                  >
                    <Twitter size={20} />
                  </button>
                  <button 
                    className="p-2 bg-[#0077b5] text-white rounded hover:bg-opacity-90 transition-colors" 
                    onClick={() => handleShare('linkedin')}
                    aria-label="Deel op LinkedIn"
                  >
                    <Linkedin size={20} />
                  </button>
                  <button 
                    className="p-2 bg-gray-700 text-white rounded hover:bg-opacity-90 transition-colors" 
                    onClick={() => handleShare('copy')}
                    aria-label="Kopieer link"
                  >
                    <Share size={20} />
                  </button>
                </div>
              </div>
            </div>
            
            {/* Sidebar */}
            <div className="lg:col-span-1">
              {/* Author box */}
              <div className="bg-brand-lightBlue rounded-lg p-6 mb-8">
                <h3 className="font-semibold mb-4">Over de auteur</h3>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-gray-300 overflow-hidden">
                    <img 
                      src="/lovable-uploads/9a314f34-d262-4ba8-a90e-97e2a1233907.png" 
                      alt="Bert Lambrecht" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <p className="font-medium">{post.author}</p>
                    <p className="text-sm text-gray-600">Content Specialist</p>
                  </div>
                </div>
                <p className="text-sm mt-4 text-justify">
                  Schrijft over B2B sales, leadgeneratie en groeistrategieën voor Vlaamse KMO's.
                </p>
              </div>
              
              {/* Related posts */}
              <div className="mb-8">
                <h3 className="font-semibold mb-4">Gerelateerde artikelen</h3>
                <div className="space-y-4">
                  {blogPosts
                    .filter(relatedPost => relatedPost.id !== post.id)
                    .slice(0, 3)
                    .map(relatedPost => (
                      <Link 
                        key={relatedPost.id}
                        to={`/blog/${relatedPost.id}`} 
                        className="flex gap-3 group"
                      >
                        <div className="flex-shrink-0 w-20 h-16 overflow-hidden rounded">
                          <img 
                            src={relatedPost.image} 
                            alt={relatedPost.title} 
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                          />
                        </div>
                        <div>
                          <h4 className="text-sm font-medium leading-tight group-hover:text-brand-blue transition-colors">
                            {relatedPost.title}
                          </h4>
                          <p className="text-xs text-gray-500 mt-1">{relatedPost.date}</p>
                        </div>
                      </Link>
                    ))}
                </div>
                <Link 
                  to="/blog" 
                  className="inline-flex items-center gap-1 text-brand-blue hover:underline text-sm font-medium mt-4"
                >
                  Alle artikelen
                  <ArrowLeft size={14} className="transform rotate-180" />
                </Link>
              </div>
              
              {/* Newsletter signup */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="font-semibold mb-2">Blijf op de hoogte</h3>
                <p className="text-sm text-gray-600 mb-4 text-justify">
                  Ontvang onze nieuwste artikelen en tips direct in je inbox.
                </p>
                <form className="space-y-3" onSubmit={handleNewsletterSubmit}>
                  <input 
                    type="email" 
                    placeholder="Jouw e-mailadres" 
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-brand-blue"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isSubmitting}
                    required
                  />
                  <button 
                    type="submit"
                    className="w-full bg-brand-blue text-white py-2 px-4 rounded hover:bg-brand-blue/90 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Subscribing...' : 'Inschrijven'}
                  </button>
                </form>
                <p className="text-xs text-gray-500 mt-2 text-justify">
                  Je kan je op elk moment uitschrijven.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Related Posts Section */}
      <section className="bg-brand-gray py-12">
        <div className="container">
          <h2 className="text-2xl md:text-3xl font-bold mb-8">Ook interessant</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {blogPosts
              .filter(relatedPost => relatedPost.id !== post.id)
              .slice(0, 3)
              .map(relatedPost => (
                <div key={relatedPost.id} className="bg-white rounded-lg overflow-hidden shadow-md">
                  <Link to={`/blog/${relatedPost.id}`} className="block h-48 overflow-hidden">
                    <img 
                      src={relatedPost.image} 
                      alt={relatedPost.title} 
                      className="w-full h-full object-cover transition-transform hover:scale-105"
                    />
                  </Link>
                  <div className="p-6">
                    <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        <span>{relatedPost.date}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        <span>{relatedPost.readTime}</span>
                      </div>
                    </div>
                    
                    <h3 className="text-xl font-semibold mb-3 text-gray-900">
                      <Link to={`/blog/${relatedPost.id}`} className="hover:text-brand-blue transition-colors">
                        {relatedPost.title}
                      </Link>
                    </h3>
                    
                    <p className="text-gray-600 mb-4 text-justify">
                      {relatedPost.excerpt}
                    </p>
                    
                    <Link to={`/blog/${relatedPost.id}`} className="inline-flex items-center gap-1 text-brand-blue hover:underline text-sm font-medium">
                      Lees meer
                      <ArrowLeft size={14} className="transform rotate-180" />
                    </Link>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </section>

      <CTASection />
      <Footer />
    </div>
  );
};

export default BlogPost;
